// src/components/SellerHeader.tsx
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext'; 
import logo from '../../assets/TradyFroglogo.png';
import { 
    Menu, 
    Search, 
    Bell, 
    MessageSquare,
    LogOut,
    ChevronDown
} from 'lucide-react';

const SellerHeader: React.FC<{ toggleSidebar: () => void }> = ({ toggleSidebar }) => {
    const { user, logout } = useUser();
    const navigate = useNavigate();
    const sellerName = user?.username || "Nilesh";

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <header className="fixed top-0 left-0 w-full bg-white/95 backdrop-blur-md shadow-sm z-50 h-[64px] flex items-center justify-between px-4 md:px-6 border-b border-gray-200">
            
            {/* --- Left: Toggle & Brand --- */}
            <div className="flex items-center gap-3 md:gap-4">
                <button 
                    onClick={toggleSidebar} 
                    className="p-2 hover:bg-teal-50 text-[#00635e] rounded-xl transition-all duration-200 active:scale-95"
                >
                    <Menu size={24} strokeWidth={2.5} />
                </button>

<Link to="/seller" className="flex items-center gap-3">
  {/* Logo */}
  <img
    src={logo}
    alt="Logo"
    className="h-18 w-18 md:h-20 md:w-20 object-contain"
  />

  {/* Brand Name */}
  <span className="text-lg md:text-xl font-black text-[#00635e] tracking-tight hidden md:block">
    Trady<span className="text-red-600">Frog</span>
  </span>

  {/* Mobile Only */}
  {/* <span className="text-lg font-black text-[#00635e] md:hidden">
    TF
  </span> */}
</Link>

            </div>

            {/* --- Center: Search Bar (Desktop) --- */}
            <div className="hidden md:flex items-center flex-1 max-w-lg mx-6">
                <div className="relative w-full group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search size={18} className="text-gray-400 group-focus-within:text-[#00635e] transition-colors" />
                    </div>
                    <input 
                        type="text" 
                        placeholder="Search products, orders, or help..." 
                        className="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-full leading-5 bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#00635e]/20 focus:border-[#00635e] transition-all duration-200 sm:text-sm"
                    />
                </div>
            </div>

            {/* --- Right: Icons & User Profile --- */}
            <div className="flex items-center gap-3 md:gap-5">
                
                {/* Notification Icons */}
                <div className="flex items-center gap-2 border-r border-gray-200 pr-4">
                    <button className="p-2 text-gray-500 hover:text-[#00635e] hover:bg-teal-50 rounded-full transition-all relative">
                        <MessageSquare size={20} />
                        <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border border-white"></span>
                    </button>
                    <button className="p-2 text-gray-500 hover:text-[#00635e] hover:bg-teal-50 rounded-full transition-all relative">
                        <Bell size={20} />
                        <span className="absolute top-2 right-2.5 flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600 border border-white"></span>
                        </span>
                    </button>
                </div>

                {/* User Profile Section (Restored) */}
                <div className="flex items-center gap-3 group cursor-pointer relative">
                    {/* Text Details (Hidden on mobile) */}
                    <div className="hidden md:flex flex-col items-end">
                        <span className="text-sm font-bold text-gray-800 leading-none mb-1">{sellerName}</span>
                        <div className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                            <span className="text-[10px] text-green-600 font-bold uppercase tracking-wide">Online</span>
                        </div>
                    </div>

                    {/* Avatar */}
                    <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-[#00635e] to-teal-400 flex items-center justify-center text-white font-bold shadow-md border-2 border-white">
                        {sellerName.charAt(0)}
                    </div>

                    {/* Logout Button (Icon for sleekness) */}
                    <button 
                        onClick={handleLogout}
                        className="p-2 ml-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        title="Logout"
                    >
                        <LogOut size={20} />
                    </button>
                </div>
            </div>
        </header>
    );
};

export default SellerHeader;